import { useState } from "react";
import * as Icons from "lucide-react";
import { AllisonBriefing } from "@/components/AllisonBriefing";
import { SECTION_META } from "./reviewMeta";
import { RecordUpdatesSection } from "./RecordUpdatesSection";
import { ReviewList } from "./ReviewList";
import { EngagementBreakdown } from "@/components/EngagementBreakdown";
import { equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";

const ESTIMATE_DISPOSITION_REASON_LABELS = {
  existing_engagement: "Existing engagement / previously authorized work",
  estimate_already_provided: "Estimate already provided",
  work_already_paid: "Work already paid",
  included_in_existing_service: "Included in existing service or package",
  no_additional_billable_services: "No additional billable services identified",
  client_declined_additional_services: "Client declined additional services",
  duplicate_or_superseded_review: "Duplicate / superseded review",
  administrative_or_non_billable_meeting: "Administrative / non-billable meeting",
  other: "Other",
};

function EstimateSection({ data, actions }) {
  const disposition = data.estimate_disposition;
  const isApproved = !!data.approved;
  const [panelOpen, setPanelOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSet = async () => {
    if (!reasonCode) return;
    if (reasonCode === "other" && !note.trim()) return;
    setSaving(true);
    try {
      await actions.setEstimateDisposition(reasonCode, note.trim() || null);
      setPanelOpen(false);
      setReasonCode("");
      setNote("");
    } finally {
      setSaving(false);
    }
  };

  if (disposition?.status === "not_required") {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="ca-estimate-disposition-set">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-emerald-900" data-testid="ca-estimate-disposition-title">
                  Estimate — Not Required
                </div>
                <div className="text-sm text-emerald-800 mt-0.5" data-testid="ca-estimate-disposition-reason">
                  {ESTIMATE_DISPOSITION_REASON_LABELS[disposition.reason_code] || disposition.reason_code}
                </div>
                {disposition.note && (
                  <div className="text-sm text-emerald-700 mt-1 italic" data-testid="ca-estimate-disposition-note">
                    "{disposition.note}"
                  </div>
                )}
                <div className="text-xs text-emerald-600/80 mt-1.5 flex items-center gap-2" data-testid="ca-estimate-disposition-meta">
                  <Icons.User className="h-3.5 w-3.5 shrink-0" />
                  <span>{disposition.by_name || "Staff"}</span>
                  {disposition.at && (
                    <>
                      <span className="text-emerald-400">·</span>
                      <span>{new Date(disposition.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            {!isApproved && (
              <button
                onClick={() => actions.clearEstimateDisposition()}
                data-testid="ca-estimate-disposition-undo"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-100 rounded-lg px-3 py-1.5 transition-colors"
              >
                <Icons.Undo2 className="h-3.5 w-3.5" />
                Undo
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400">Proposal draft preserved below for reference only.</p>
        <textarea
          value={data.proposal_draft || ""}
          onChange={(e) => actions.setField("proposal_draft", e.target.value)}
          onBlur={() => actions.saveField("proposal_draft")}
          data-testid="ca-proposal"
          className="w-full min-h-[200px] text-sm leading-relaxed rounded-lg border border-slate-200 px-3 py-2.5 bg-slate-50 text-slate-400 resize-none focus:outline-none"
          readOnly
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <textarea
        value={data.proposal_draft || ""}
        onChange={(e) => actions.setField("proposal_draft", e.target.value)}
        onBlur={() => actions.saveField("proposal_draft")}
        data-testid="ca-proposal"
        className="w-full min-h-[460px] text-sm leading-relaxed rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
      />

      {!panelOpen ? (
        <button
          onClick={() => setPanelOpen(true)}
          data-testid="ca-estimate-disposition-open"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 hover:border-slate-400 rounded-lg px-3 py-2 transition-colors"
        >
          <Icons.Ban className="h-4 w-4" />
          No New Estimate Required
        </button>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="ca-estimate-disposition-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">No New Estimate Required</div>
            <button
              onClick={() => { setPanelOpen(false); setReasonCode(""); setNote(""); }}
              data-testid="ca-estimate-disposition-close"
              className="text-slate-400 hover:text-slate-600"
            >
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Reason <span className="text-rose-500">*</span>
            </label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              data-testid="ca-estimate-disposition-reason-select"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400/40 bg-white"
            >
              <option value="">Select a reason…</option>
              {Object.entries(ESTIMATE_DISPOSITION_REASON_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">
              Note{" "}
              {reasonCode === "other"
                ? <span className="text-rose-500">* required</span>
                : <span className="text-slate-400">(optional)</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="ca-estimate-disposition-note-input"
              rows={3}
              placeholder={reasonCode === "other" ? "Explain why no estimate is required…" : "Optional additional context…"}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-400/40"
            />
          </div>
          {reasonCode === "other" && !note.trim() && (
            <p className="text-xs text-rose-600" data-testid="ca-estimate-disposition-note-required">
              A note is required when reason is "Other".
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSet}
              disabled={!reasonCode || (reasonCode === "other" && !note.trim()) || saving}
              data-testid="ca-estimate-disposition-confirm"
              className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#171B2E] hover:bg-[#222842] text-white rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}
              Confirm — No Estimate Required
            </button>
            <button
              onClick={() => { setPanelOpen(false); setReasonCode(""); setNote(""); }}
              data-testid="ca-estimate-disposition-cancel"
              className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// data = the analysis record (read-only view)
// model = { total, reviewedCount, reviewableCount, lowConfidence, isRejected, firstUnfinished,
//           pendingUpdates, attentionKeys, reviewableKeys, autoUpdates, proposedUpdates,
//           acceptedUpdates, approving, rejecting, statusOf, confidence }
// actions = { patch, updateService, removeService, addService, setField, saveField,
//             acceptUpdate, rejectUpdate, openSection, requestApprove, reject, reanalyze, saveSummary }
export function ReviewSectionBody({ data, activeK, model, actions, currencyCtx }) {
  const {
    total, reviewedCount, reviewableCount, lowConfidence, isRejected, firstUnfinished,
    pendingUpdates, attentionKeys, reviewableKeys, autoUpdates, proposedUpdates,
    acceptedUpdates, approving, rejecting, statusOf, confidence,
  } = model;
  const fx = currencyCtx || { usd_thb_rate: 33, show_thb: false };

  switch (activeK) {
    case "record_updates":
      return (
        <RecordUpdatesSection
          model={{ autoUpdates, proposedUpdates, pendingUpdates, acceptedUpdates }}
          actions={{ onAccept: actions.acceptUpdate, onReject: actions.rejectUpdate }}
        />
      );
    case "summary":
      return <AllisonBriefing value={data.internal_summary || ""} clientInfo={data.client_info} confidence={data.confidence} complexity={data.complexity} onSave={actions.saveSummary} />;
    case "services": {
      const optional = data.optional_services || [];
      const cxColor = { simple: "bg-slate-100 text-slate-600", moderate: "bg-amber-100 text-amber-700", complex: "bg-rose-100 text-rose-700" };
      const unresolvedCount = (data.recommended_services || []).filter(
        s => s.pricing_evidence?.has_conflict && !s.pricing_evidence?.staff_confirmed
      ).length;
      return (
        <div className="space-y-5" data-testid="ca-services">
          <div className="flex items-center justify-between pb-1">
            <span className="text-sm font-bold uppercase tracking-wide text-slate-500">Required Services</span>
            <span className="text-lg font-bold text-slate-900" data-testid="ca-total">
              Total: ${total.toLocaleString()}{unresolvedCount > 0 && <span className="text-sm font-normal text-amber-600 ml-1">+ {unresolvedCount} unresolved</span>}
            </span>
          </div>
          {unresolvedCount > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2 text-xs text-amber-800" data-testid="ca-pricing-conflict-banner">
              <Icons.AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
              <span><strong>{unresolvedCount} service{unresolvedCount > 1 ? "s" : ""}</strong> require pricing confirmation before the estimate can be created.</span>
            </div>
          )}
          {(data.recommended_services || []).length === 0 && <p className="text-sm text-slate-400">No required services recommended.</p>}
          {(data.recommended_services || []).map((s, i) => {
            const hasEvidence = !!s.pricing_evidence;
            const isConflict = hasEvidence && s.pricing_evidence.has_conflict;
            const conflictResolved = isConflict && s.pricing_evidence.staff_confirmed;
            const hasSingleEvidence = hasEvidence && !isConflict;
            return (
            <div key={i} className={`rounded-xl border p-4 ${isConflict && !conflictResolved ? "border-amber-300 bg-amber-50/30" : "border-slate-200"}`} data-testid={`ca-service-${i}`}>
              <div className="flex items-center gap-2">
                <Icons.CheckCircle2 className={`h-4 w-4 shrink-0 ${isConflict && !conflictResolved ? "text-amber-400" : "text-emerald-500"}`} />
                <input value={s.name || ""} onChange={(e) => actions.updateService(i, "name", e.target.value)} onBlur={() => actions.saveServices()}
                  className="flex-1 text-sm rounded-md border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                {s.complexity && <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${cxColor[s.complexity] || "bg-slate-100 text-slate-600"}`} data-testid={`ca-service-${i}-complexity`}>{s.complexity}</span>}
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-sm">{(s.currency || "USD") === "THB" ? "฿" : "$"}</span>
                  <input type="number"
                    value={s.price ?? ""}
                    onChange={(e) => actions.updateService(i, "price", e.target.value)}
                    onBlur={() => actions.saveServices()}
                    data-testid={`ca-service-${i}-price`}
                    placeholder={isConflict && !conflictResolved ? "—" : "0.00"}
                    className={`w-24 text-sm rounded-md border px-2 py-1.5 focus:outline-none focus:ring-1 ${hasSingleEvidence ? "border-blue-300 focus:ring-blue-400 text-blue-800 font-medium" : isConflict && !conflictResolved ? "border-amber-300 bg-amber-50 text-amber-700 placeholder-amber-400 focus:ring-amber-400" : "border-slate-200 focus:ring-blue-400"}`}
                  />
                </div>
                <button onClick={() => actions.removeService(i)} className="text-slate-300 hover:text-rose-500"><Icons.X className="h-4 w-4" /></button>
              </div>
              {equivalentFor(s.price, s.currency || "USD", { rate: fx.usd_thb_rate, showThb: fx.show_thb }) && (
                <div className="text-[11px] text-slate-400 text-right pr-6 mt-0.5" data-testid={`ca-service-${i}-equiv`}>≈ {equivalentFor(s.price, s.currency || "USD", { rate: fx.usd_thb_rate, showThb: fx.show_thb })}</div>
              )}
              {(s.pricing_rationale || s.price_range || s.confidence) && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5 pl-6 text-[11px] text-slate-500">
                  {s.pricing_rationale && <span data-testid={`ca-service-${i}-rationale`}>{s.pricing_rationale}</span>}
                  {s.price_range && <span className="text-slate-400">Range ${s.price_range.min}–{s.price_range.max}</span>}
                  {s.confidence ? <span className="text-violet-600 font-medium">{s.confidence}% conf.</span> : null}
                  {s.requires_manual_price && !isConflict && <span className="text-amber-600 font-medium">Set price manually</span>}
                </div>
              )}

              {/* Single evidence-backed price — show library vs proposed price context */}
              {hasSingleEvidence && (
                <div className="mt-2.5 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2 text-[11px] space-y-1" data-testid={`ca-service-${i}-evidence`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-400">Standard Service Library Price:</span>
                    <span className="font-medium text-slate-500">${(s.library_price ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icons.MessageSquare className="h-3 w-3 text-blue-500 shrink-0" />
                      <span className="text-blue-700 font-semibold">Proposed Client Price</span>
                    </div>
                    <span className="font-bold text-blue-700" data-testid={`ca-service-${i}-evidence-amount`}>${(s.pricing_evidence.amount ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-start gap-1 text-slate-400 leading-tight">
                    <Icons.Link className="h-3 w-3 shrink-0 mt-px" />
                    <span>
                      {s.pricing_evidence.source_label || "Historical Client Communication"}
                      {s.pricing_evidence.conversation_title ? ` · ${s.pricing_evidence.conversation_title}` : ""}
                      {s.pricing_evidence.message_number ? ` · msg #${s.pricing_evidence.message_number}` : ""}
                      {s.pricing_evidence.date ? ` · ${(s.pricing_evidence.date || "").slice(0, 10)}` : ""}
                    </span>
                  </div>
                  {s.pricing_evidence.accepted_quote && (
                    <div className="text-slate-400 italic truncate" data-testid={`ca-service-${i}-evidence-quote`}>"{s.pricing_evidence.accepted_quote.slice(0, 120)}"</div>
                  )}
                </div>
              )}

              {/* Conflict — unresolved: staff must select a price */}
              {isConflict && !conflictResolved && (
                <div className="mt-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`ca-service-${i}-conflict`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icons.AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                    <span className="text-xs font-bold text-amber-800">Pricing Confirmation Required</span>
                  </div>
                  <div className="text-[11px] text-slate-600 mb-2.5">
                    Standard Service Library Price: <span className="font-semibold">${(s.library_price ?? 0).toLocaleString()}</span>
                    <span className="ml-2 text-slate-400">· Evidence amounts differ — please select the correct price.</span>
                  </div>
                  <div className="space-y-1.5 mb-2.5">
                    {(s.pricing_evidence.all_evidence_items || []).map((ev, ei) => (
                      <div key={ei} className="flex items-center justify-between gap-2 bg-white rounded border border-amber-200 px-2.5 py-2">
                        <div className="min-w-0">
                          <div className="text-[11px] font-medium text-slate-700">
                            {ev.source_label || "Historical Client Communication"}
                            {ev.date ? ` · ${(ev.date || "").slice(0, 10)}` : ""}
                            {ev.conversation_title ? ` · ${ev.conversation_title}` : ""}
                          </div>
                          {ev.accepted_quote && (
                            <div className="text-[10px] text-slate-500 truncate max-w-xs">"{ev.accepted_quote.slice(0, 100)}"</div>
                          )}
                        </div>
                        <button
                          onClick={() => actions.resolveConflictPrice(i, ev.amount, false)}
                          className="shrink-0 text-xs font-semibold bg-[#171B2E] hover:bg-[#222842] text-white rounded-lg px-2.5 py-1.5 transition-colors"
                          data-testid={`ca-service-${i}-conflict-select-${ei}`}
                        >
                          Select ${ev.amount.toLocaleString()}
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-500 shrink-0">Or enter amount:</span>
                    <input
                      type="number" min="0" step="0.01" placeholder="Enter price"
                      className="w-28 text-xs border border-amber-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      data-testid={`ca-service-${i}-conflict-manual`}
                      onBlur={(e) => { if (e.target.value) { actions.resolveConflictPrice(i, parseFloat(e.target.value), true); e.target.value = ""; } }}
                      onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { actions.resolveConflictPrice(i, parseFloat(e.target.value), true); e.target.value = ""; } }}
                    />
                  </div>
                </div>
              )}

              {/* Conflict — resolved: show confirmation acknowledgement */}
              {conflictResolved && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700 pl-1" data-testid={`ca-service-${i}-conflict-resolved`}>
                  <Icons.CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Pricing confirmed at ${(s.price ?? 0).toLocaleString()}
                    {s.pricing_evidence.staff_resolution === "manual_entry" ? " · manual entry" : " · evidence selected"}
                  </span>
                </div>
              )}
            </div>
          );})}  
          <button onClick={actions.addService} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"><Icons.Plus className="h-3.5 w-3.5" />Add service</button>

          {/* Potential Additional Services — NEVER billed until staff explicitly add them */}
          {optional.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="ca-optional-services">
              <div className="flex items-center gap-2 mb-2">
                <Icons.Lightbulb className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Potential Additional Services</span>
                <span className="text-[11px] text-slate-400">(not included — staff decision)</span>
              </div>
              <div className="space-y-2">
                {optional.map((o, i) => (
                  <div key={i} className="rounded-lg border border-dashed border-amber-300 bg-amber-50/50 p-2.5" data-testid={`ca-optional-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800">{o.name}</div>
                        {o.reason && <p className="text-xs text-slate-600 mt-0.5">{o.reason}</p>}
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                          {o.suggested_range && <span>Est. range ${o.suggested_range.min}–{o.suggested_range.max}</span>}
                          {o.confidence ? <span className="text-violet-600 font-medium">{o.confidence}% conf.</span> : null}
                          <span className="text-slate-400">Not billed</span>
                        </div>
                      </div>
                      <button onClick={() => actions.addOptional(i)} data-testid={`ca-optional-${i}-add`}
                        className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold bg-[#171B2E] hover:bg-[#222842] text-white rounded-lg px-3 py-1.5">
                        <Icons.Plus className="h-3.5 w-3.5" />Add to Estimate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <EngagementBreakdown breakdown={data.engagement_breakdown} rate={fx.usd_thb_rate} showThb={fx.show_thb} />
          {(fx.show_thb || (data.recommended_services || []).some((s) => (s.currency || "USD") === "THB")) && !(data.engagement_breakdown && Object.keys(data.engagement_breakdown).length > 0) && <FxDisclaimer className="mt-2" />}
        </div>
      );
    }
    case "tax_situation":
      return <ReviewList items={data.tax_situation} tone="blue" emptyMsg="No tax situation notes." testid="ca-tax" />;
    case "risks":
      return <ReviewList items={data.risks} tone="rose" emptyMsg="No risk flags identified." testid="ca-risks" />;
    case "missing_docs":
      return (
        <div>
          <ReviewList items={data.missing_documents} tone="doc" emptyMsg="No missing documents." testid="ca-missing" />
          <p className="text-xs text-slate-400 mt-3">These become document requests in the client portal on approval.</p>
        </div>
      );
    case "estimate":
      return <EstimateSection data={data} actions={actions} />;
    case "email":
      return <textarea value={data.client_email_draft || ""} onChange={(e) => actions.setField("client_email_draft", e.target.value)} onBlur={() => actions.saveField("client_email_draft")}
        data-testid="ca-email" className="w-full min-h-[420px] text-sm leading-relaxed rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/40" />;
    case "engagement":
      return <textarea value={data.engagement_draft || ""} onChange={(e) => actions.setField("engagement_draft", e.target.value)} onBlur={() => actions.saveField("engagement_draft")}
        data-testid="ca-engagement" className="w-full min-h-[460px] text-sm leading-relaxed rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/40" />;
    case "approval": {
      const unresolvedConflictCount = model.unresolvedPricingConflicts || 0;
      const hasDisposition = data.estimate_disposition?.status === "not_required";
      return (
        <div data-testid="ca-approval">
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"><div className="text-[11px] text-slate-400">Estimate Total</div><div className="text-2xl font-bold text-slate-900">{hasDisposition ? <span className="text-slate-400 text-base font-semibold">Not required</span> : <>${total.toLocaleString()}{unresolvedConflictCount > 0 && <span className="text-sm font-normal text-amber-500 ml-1">+?</span>}</>}</div></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"><div className="text-[11px] text-slate-400">Confidence</div><div className={`text-2xl font-bold ${lowConfidence ? "text-amber-600" : "text-emerald-600"}`}>{confidence}%</div></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"><div className="text-[11px] text-slate-400">Review Progress</div><div className="text-2xl font-bold text-slate-900">{reviewedCount}/{reviewableCount}</div></div>
          </div>
          {!hasDisposition && unresolvedConflictCount > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 mb-5 text-sm" data-testid="ca-approval-conflict-block">
              <div className="font-semibold text-rose-800 flex items-center gap-1.5 mb-1"><Icons.AlertTriangle className="h-4 w-4" />Pricing Confirmation Required — Cannot Approve</div>
              <p className="text-rose-700">{unresolvedConflictCount} service{unresolvedConflictCount > 1 ? "s have" : " has"} conflicting historical evidence. Go to <button onClick={() => actions.openSection("services")} className="underline font-medium">Services</button> and confirm a final price before approving.</p>
            </div>
          )}
          {(firstUnfinished || pendingUpdates.length > 0 || attentionKeys.length > 0) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-5 text-sm" data-testid="ca-approval-warnings">
              <div className="font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5"><Icons.AlertTriangle className="h-4 w-4" />Before you approve</div>
              <ul className="space-y-1 text-amber-700">
                {reviewableKeys.filter((k) => statusOf(k) !== "reviewed").map((k) => (
                  <li key={k} className="flex items-center gap-1.5"><Icons.Dot className="h-4 w-4 -ml-1" /><button onClick={() => actions.openSection(k)} className="underline hover:no-underline">{SECTION_META[k].label}</button> is not marked reviewed.</li>
                ))}
                {pendingUpdates.length > 0 && <li className="flex items-center gap-1.5"><Icons.Dot className="h-4 w-4 -ml-1" />{pendingUpdates.length} client record update{pendingUpdates.length > 1 ? "s" : ""} still awaiting your decision.</li>}
              </ul>
            </div>
          )}
          {hasDisposition ? (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 mb-4 text-sm" data-testid="ca-approval-no-estimate-notice">
              <div className="font-semibold text-blue-800 flex items-center gap-1.5 mb-1">
                <Icons.Info className="h-4 w-4" />No estimate or proposal will be created
              </div>
              <p className="text-blue-700">This review is marked as No New Estimate Required. Completing the review closes the meeting card and updates the client record — no estimate, proposal, engagement letter, or email will be sent to the client.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-4">Approving creates the estimate, seeds document requests, emails the estimate to the client, and advances the workflow to <b>Estimate Sent</b>.</p>
          )}
          {isRejected ? (
            <button onClick={() => actions.reanalyze()} data-testid="ca-reanalyze-footer" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-5 py-2.5"><Icons.Sparkles className="h-4 w-4" />Ask Allison to Re-analyze</button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={actions.requestApprove}
                disabled={approving || rejecting || (!hasDisposition && unresolvedConflictCount > 0)}
                data-testid="ca-approve-final"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle2 className="h-4 w-4" />}
                {hasDisposition ? "Complete Review — No Estimate" : "Approve & Send Estimate"}
              </button>
              <button onClick={actions.reject} disabled={rejecting || approving} data-testid="ca-reject-final" className="inline-flex items-center gap-2 border border-rose-200 text-rose-700 hover:bg-rose-50 font-semibold rounded-lg px-5 py-2.5 disabled:opacity-50">{rejecting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.XCircle className="h-4 w-4" />}Reject</button>
            </div>
          )}
        </div>
      );
    }
    default:
      return null;
  }
}
